"use client";

import { useState, useEffect } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

export function useIsMac() {
  const [isMac, setIsMac] = useState(true);
  useEffect(() => {
    setIsMac(/(Mac|iPhone|iPad|iPod)/i.test(navigator.userAgent));
  }, []);
  return isMac;
}

/* Each key renders as its own keycap. `min-w` matches the height so
   single-character keys are perfect squares; longer keys (Esc, Ctrl)
   keep the same height and grow only as much as the label needs. */
const kbdShortcutVariants = cva(
  "inline-flex items-center justify-center rounded border font-sans leading-none -translate-y-[0.5px]",
  {
    variants: {
      variant: {
        subtle: "border-border bg-contrast/4 text-contrast/30",
        primary:
          "border-contrast/20 bg-contrast/10 text-contrast/60 dark:text-contrast/50",
      },
      size: {
        xs: "h-4 min-w-4 px-[3px] text-[9px]",
        sm: "h-5 min-w-5 px-1 text-[10px]",
        md: "h-6 min-w-6 px-1.5 text-[11px]",

        "icon-xs": "size-4.5 px-0 text-xs",
        "icon-sm": "size-5.5 px-0 text-xs",
        "icon-md": "size-7 px-0 text-sm",
        "icon-lg": "size-9 px-0 text-base",
      },
    },
    defaultVariants: {
      variant: "subtle",
      size: "sm",
    },
  },
);

export type KbdShortcutVariants = VariantProps<typeof kbdShortcutVariants>;

interface KbdShortcutProps extends KbdShortcutVariants {
  /** Key names — use "mod" for ⌘ (Mac) / Ctrl (other). */
  keys: string[];
  className?: string;
}

export function KbdShortcut({
  keys,
  variant,
  size,
  className,
}: KbdShortcutProps) {
  const isMac = useIsMac();

  return (
    <span className={cn("inline-flex items-center gap-1", className)}>
      {keys.map((key, i) => (
        <kbd key={i} className={cn(kbdShortcutVariants({ variant, size }))}>
          {key === "mod" ? (isMac ? "⌘" : "Ctrl") : key}
        </kbd>
      ))}
    </span>
  );
}
